import { isPlatformBrowser } from '@angular/common';
import { Component, Input, ViewChild, ElementRef, OnInit, EventEmitter, Output, Inject, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Console } from 'console';
import { combineLatest, ReplaySubject } from 'rxjs';
import { ImageDownloadService } from 'src/app/core/services/image-download.service';
import { BaseFileUploadComponent } from '../../../core/components/base-file-upload.component';
import FormHelper from '../../../core/helpers/form.helper';
import { FileService } from '../../../core/services/file.service';
import { MessageService } from '../../../core/services/message.service';

@Component({
  selector: 'app-image-input',
  templateUrl: './image-input.component.html',
  styleUrls: ['./image-input.component.scss']
})
export class ImageInputComponent extends BaseFileUploadComponent implements OnInit {
  @Input() public allowedFileTypes: string[] = ['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml'];
  @Input() public formGroup!: FormGroup;
  @Input() public autoHeight = false;
  @Input() public maxWidth = 1920;
  @Input() public maxHeight = 1080;
  @Input() public ratioWidth = 16;
  @Input() public ratioHeight = 9;
  @Input() public width: number | null = null;
  @Input() public height: number | null = null;
  @Input() public isPublic = true;
  @Input() public enabled = true;
  @Input() public showMaxSizeInfo = false;
  @Input() public showFileSize = false;
  @Input() public showFileName = false;
  @Input() public uploadLabel: string = "ImageInput.UploadImage";
  @Input() public showPrimary = false;
  @Input() public showTooltip = false;
  @Input() public canRemove = true;
  @Input() public squareOnly = false;
  @Input() public isPdf = false;
  @Input() public removed = new EventEmitter<any>();
  @Output() public added = new EventEmitter<any>();
  @ViewChild('fileInput') fileInput!: ElementRef;
  file: File | null = null;
  accept: string;
  dataOrUrl$ = new ReplaySubject<string | SafeUrl | null>(1);
  currentProgress = 0;
  fileSize: string | null = null;

  constructor(private readonly messageService: MessageService,
    private readonly fileService: FileService,
    private readonly translateService: TranslateService,
    private readonly imageDownloadService: ImageDownloadService,
    @Inject(PLATFORM_ID) private readonly platformId: string
  ) {
    super();

    this.accept = this.allowedFileTypes.join(',');

    if (!this.width) {
      this.width = this.maxWidth;
    }

    if (!this.width) {
      this.height = this.maxHeight;
    }
  }

  ngOnInit(): void {
    if (this.isPdf) {
      this.showFileName = true;
      this.allowedFileTypes = ["application/pdf"];
      this.accept = this.allowedFileTypes.join(',');
    }

    // Initialize with existing data only once
    if (this.idControl?.value && this.typeControl?.value) {
      this.load(this.idControl!.value, this.typeControl!.value);
    }
    else {
      // Necessary if initial data is passed in later
      combineLatest([this.idControl?.valueChanges, this.typeControl?.valueChanges])
        .unsubscribeOnDestroy(this)
        .subscribe(() => {
          this.load(this.idControl!.value, this.typeControl!.value);
        });
    }

    this.removed.unsubscribeOnDestroy(this)
      .subscribe(response => {
        if (response) {
          this.removeFile();
        }
      });
  }

  resetImage() {
    this.dataOrUrl$.next(null);
    this.formGroup.get("id")?.setValue(null);
    this.formGroup.get("name")?.setValue("");
    this.formGroup.get("type")?.setValue("");
    this.formGroup.get("data")?.setValue("");
    this.showFileName = false;
    this.showFileSize = false;
    this.fileSize = "";
  }

  removeFile() {
    this.added.emit();
    this.resetImage();
    this.fileInput.nativeElement.value = null;
  }

  load(id: string, type: string): void {
    if (id && type) {
      if (isPlatformBrowser(this.platformId)) {
        this.imageDownloadService.getImageUrl(id, type)
          .unsubscribeOnDestroy(this)
          .subscribe(url => {
            this.dataOrUrl$.next(url);
          });
      }
    }
  }

  uploadFile() {
    this.fileInput.nativeElement.click();
  }

  showMessage(error: string, param?: any) {
    this.messageService.openErrorTranslation(error, param);
  }
  
  get idControl(): FormControl {
    return this.formGroup?.get('id') as FormControl;
  }

  get dataControl(): FormControl {
    return this.formGroup?.get('data') as FormControl;
  }

  get typeControl(): FormControl {
    return this.formGroup?.get('type') as FormControl;
  }

  get nameControl(): FormControl {
    return this.formGroup?.get('name') as FormControl;
  }

  onChange(event) {
    const files = event.target.files as FileList;

    this.handleFileInput(files);
  }

  protected handleFileInput(files: FileList): void {
    if (!this.enabled) {
      return;
    }

    if (files?.length) {
      const fileType: string = files[0].type;

      if (this.allowedFileTypes.indexOf(fileType) === -1) {
        this.showMessage('ImageInput.InvalidType');
      }
      else if (files.length > 1) {
        this.showMessage('ImageInput.ChooseSingleFile');
      }
      else {
        const reader = new FileReader();
        const file = files[0] as File;
        this.file = file;

        if (!this.isPdf) {
          reader.onload = (e: any) => {
            const result: any = reader.result;

            const image = new Image();
            image.src = e.target.result;
            image.onload = rs => {
              if (!rs?.currentTarget) {
                return;
              }
    
              const img_height = rs.currentTarget['height'];
              const img_width = rs.currentTarget['width'];
              
              if(this.squareOnly){
                if(img_height !== img_width){
                  this.showMessage('ImageInput.SquareOnly');
                  return;
                }
              }
    
              if (img_height > this.maxHeight || img_width > this.maxWidth) {
                this.file = null;
                this.showMessage('ImageInput.MaximumImageSize', { width: this.maxWidth, height: this.maxHeight });
              }
              else {
                this.formGroup = FormHelper.createFileFormGroup();
                this.formGroup.patchValue({
                  data: result.substr(result.indexOf(',') + 1),
                  type: file.type,
                  name: file.name
                });
    
                this.fileSize = FormHelper.getFileSize(file.size).toString();
                this.dataOrUrl$.next(result as string);
                this.added.emit(this.formGroup.value);
              }
            }
          }  
        } else {
          reader.onloadend = () => {
            var base64String = reader.result!.toString();
            var base64resultString = base64String.substring(
              base64String.indexOf(',') + 1
            );

            this.formGroup = FormHelper.createFileFormGroup();
            this.formGroup.patchValue({
              data: base64resultString,
              type: file.type,
              name: file.name
            });

            this.fileSize = FormHelper.getFileSize(file.size).toString();
            this.dataOrUrl$.next(base64resultString);
            this.added.emit(this.formGroup.value);
          }
        };

        reader.onerror = (subEvent: Event) => {
          const fileReader = subEvent.target as FileReader;

          if (fileReader.error?.message) {
            this.showMessage(fileReader.error?.message, null);
          }
          
          this.file = null;
        };

        reader.onabort = (subEvent: Event) => {
          const fileReader = subEvent.target as FileReader;

          if (fileReader.error?.message) {
            this.showMessage(fileReader.error?.message, null);
          }

          this.file = null;
        };

        reader.onprogress = (subEvent: ProgressEvent) => {
          if (subEvent.lengthComputable) {
            this.currentProgress = (subEvent.loaded / subEvent.total) * 100;
          }
        };

        if (this.file) {
          reader.readAsDataURL(this.file);
        }
      }
    }
  }
}
